import { useState } from 'react'
import { Link } from './nav'
import { ChevronDown, ChevronRight, ExternalLink, CheckCircle2, XCircle } from 'lucide-react'
import { ActionsList } from './JourneysTab'
import type { CreatedJourney } from '../data/projectTypes'
import './CreatedJourneysList.css'

// Shared across the unified task-detail renderer: the collapsible list of journeys a run created
// or updated (with judge status + steps).
function JudgeStatusBadge({ status }: { status: string | null }) {
  if (!status) return null
  const isApproved = status === 'approved'
  return (
    <span className={`created-journeys__judge-badge created-journeys__judge-badge--${status}`}>
      {isApproved ? <CheckCircle2 size={12} aria-hidden /> : <XCircle size={12} aria-hidden />}
      {status}
    </span>
  )
}

export function CreatedJourneysList({ journeys, projectId, heading, journeyLinkId }: { journeys: CreatedJourney[]; projectId: string; heading?: string; journeyLinkId?: string }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const approved = journeys.filter(j => j.judge_status !== 'rejected')
  const rejected = journeys.filter(j => j.judge_status === 'rejected')
  return (
    <section className="created-journeys__section" data-testid="polish-created-journeys">
      <h2>{heading ?? 'Created Journeys'} ({approved.length}){rejected.length > 0 && <span className="created-journeys__rejected-count"> · {rejected.length} rejected</span>}</h2>
      <div className="created-journeys__list">
        {approved.map(j => {
          const isOpen = expanded.has(j.id)
          const latestSub = j.judge_submissions?.length ? j.judge_submissions[j.judge_submissions.length - 1] : null
          return (
            <div key={j.id} className="created-journeys__item">
              <button
                type="button"
                className="created-journeys__header"
                onClick={() => toggle(j.id)}
                data-testid={`created-journey-toggle-${j.id}`}
                data-analytics-event="polish-created-journey-toggle"
              >
                <span className="created-journeys__expand">
                  {isOpen ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
                </span>
                <span className="created-journeys__name">{j.name}</span>
                <JudgeStatusBadge status={j.judge_status} />
                {j.step_count > 0 && (
                  <span className="created-journeys__count">{j.step_count} steps</span>
                )}
                <Link
                  to={`/projects/${projectId}/journeys/${journeyLinkId ?? j.id}`}
                  className="created-journeys__link"
                  onClick={e => e.stopPropagation()}
                  data-analytics-event="polish-created-journey-link"
                >
                  View <ExternalLink size={12} />
                </Link>
              </button>
              {isOpen && (
                <>
                  {j.description && (
                    <p className="created-journeys__description">{j.description}</p>
                  )}
                  {j.step_count > 0 && (
                    <div className="created-journeys__steps">
                      <ActionsList steps={j.steps} stepCount={j.step_count} />
                    </div>
                  )}
                  {latestSub && (
                    <div className="created-journeys__assessment">
                      <p>{latestSub.result.assessment}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
        {rejected.length > 0 && (
          <details className="created-journeys__rejected-section">
            <summary className="created-journeys__rejected-summary">
              {rejected.length} rejected attempt{rejected.length !== 1 ? 's' : ''}
            </summary>
            <div className="created-journeys__rejected-list">
              {rejected.map(j => {
                const isOpen = expanded.has(j.id)
                const latestSub = j.judge_submissions?.length ? j.judge_submissions[j.judge_submissions.length - 1] : null
                return (
                  <div key={j.id} className="created-journeys__item created-journeys__item--rejected">
                    <button
                      type="button"
                      className="created-journeys__header"
                      onClick={() => toggle(j.id)}
                      data-testid={`created-journey-toggle-${j.id}`}
                      data-analytics-event="polish-created-journey-toggle"
                    >
                      <span className="created-journeys__expand">
                        {isOpen ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
                      </span>
                      <span className="created-journeys__name">{j.name}</span>
                      <JudgeStatusBadge status={j.judge_status} />
                      {j.step_count > 0 && (
                        <span className="created-journeys__count">{j.step_count} steps</span>
                      )}
                    </button>
                    {isOpen && (
                      <>
                        {j.description && (
                          <p className="created-journeys__description">{j.description}</p>
                        )}
                        {j.step_count > 0 && (
                          <div className="created-journeys__steps">
                            <ActionsList steps={j.steps} stepCount={j.step_count} />
                          </div>
                        )}
                        {latestSub && (
                          <div className="created-journeys__assessment created-journeys__assessment--rejected">
                            <p className="created-journeys__assessment-text">{latestSub.result.assessment}</p>
                            {latestSub.result.problems?.length > 0 && (
                              <ul className="created-journeys__problems">
                                {latestSub.result.problems.map((p, i) => <li key={i}>{p}</li>)}
                              </ul>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </details>
        )}
      </div>
    </section>
  )
}
